import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  CssBaseline,
  ThemeProvider,
  Typography,
  useMediaQuery,
  Stack
} from '@mui/material';
import { standaloneTheme } from './theme';
import QRCode from 'qrcode.react';
import CurrentLottery from 'src/content/dashboards/Lottery/CurrentLottery';
import announcementImg from 'src/content/pages/external/gs3.png';
import { Lottery } from 'src/util/types';
import Marquee from 'react-fast-marquee';
import Logo from 'src/components/LogoSign';

type GalleryImage = {
  src: string;
  title?: string;
  id?: string;
};

type LotteryViewProps = {
  lottery: Lottery | null;
  galleryImages?: GalleryImage[];
  loading: boolean;
  error?: string | null;
  variant?: 'default' | 'vibrant' | 'channel';
};

export const heroBackground = (variant: 'default' | 'vibrant' | 'channel') => {
  if (variant === 'vibrant') {
    return {
      backgroundImage: 'radial-gradient(circle at center, rgba(10,10,10,0.95) 0%, rgba(5,5,5,0.98) 70%)',
      position: 'relative'
    };
  }
  if (variant === 'channel') {
    return {
      backgroundImage: 'radial-gradient(circle at top, rgba(13,17,23,0.9), rgba(2,2,3,0.98))',
      position: 'relative'
    };
  }
  return {
    backgroundImage: `radial-gradient(circle at top, rgba(255,255,255,0.12), transparent 60%), url(${announcementImg})`,
    backgroundBlendMode: 'overlay',
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  };
};

function HeroView({
  lottery,
  galleryImages = [],
  variant = 'default'
}: {
  lottery: Lottery;
  galleryImages: GalleryImage[];
  variant?: 'default' | 'vibrant' | 'channel';
}) {
  const isMobile = useMediaQuery(standaloneTheme.breakpoints.down('sm'));
  const qrCodeVal = `${window.location.origin}/join/${lottery.id}`;
  const prizeIcons = ['🎁', '🎉', '🏆', '💎', '🎮', '🎧'];
  const channelIconStyles = [
    { background: 'linear-gradient(135deg, #0ba360, #3cba92)', glow: 'rgba(11,163,96,0.55)', icon: '💶' },
    { background: 'linear-gradient(135deg, #1f6feb, #4cc9f0)', glow: 'rgba(76,201,240,0.55)', icon: '🎟️' },
    { background: 'linear-gradient(135deg, #ff758c, #ff7eb3)', glow: 'rgba(255,126,179,0.55)', icon: '🎁' },
    { background: 'linear-gradient(135deg, #36d1dc, #5b86e5)', glow: 'rgba(91,134,229,0.55)', icon: '✈️' }
  ];

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        color: 'text.primary',
        minHeight: '100vh',
        ...heroBackground(variant),
        '&:after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'url(/static/prizes/prize1.png), url(/static/prizes/prize2.png), url(/static/prizes/prize3.png)',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '120px, 140px, 100px',
          backgroundPosition: '20% 30%, 70% 20%, 50% 70%',
          opacity: 0.08,
          animation: 'prizeDrift 26s linear infinite',
          pointerEvents: 'none'
        },
        '@keyframes prizeDrift': {
          '0%': { transform: 'translate3d(0, 0, 0)' },
          '50%': { transform: 'translate3d(-25px, 25px, 0)' },
          '100%': { transform: 'translate3d(0, 0, 0)' }
        }
      }}
    >
      <Container component="main" maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Stack spacing={4}>
          <Box textAlign="center">
            <Logo />
          </Box>

          <Card
            sx={{
              p: { xs: 3, md: 5 },
              textAlign: 'center',
              background:
                variant === 'vibrant'
                  ? 'linear-gradient(140deg, rgba(255,255,255,0.18), rgba(5,5,5,0.6))'
                  : 'linear-gradient(160deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))',
              border: '1px solid rgba(255,255,255,0.12)',
              backdropFilter: 'blur(18px)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle, rgba(255,197,112,0.25), transparent 45%)',
                filter: 'blur(80px)'
              }}
            />
            <Stack
              spacing={3}
              position="relative"
              alignItems="center"
              sx={{
                '@keyframes floatIcon': {
                  '0%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-8px)' },
                  '100%': { transform: 'translateY(0)' }
                }
              }}
            >
              <Typography
                variant="h5"
                fontWeight={600}
                sx={{ textTransform: 'uppercase', letterSpacing: 4 }}
              >
                Mitmachen &amp; Gewinnen
              </Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="center">
                {variant === 'channel'
                  ? channelIconStyles.map((style, idx) => (
                      <Box
                        key={`channel-icon-${idx}`}
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 3,
                          background: style.background,
                          border: '1px solid rgba(255,255,255,0.2)',
                          boxShadow: `0 12px 26px ${style.glow}`,
                          animation: `floatIcon ${9 + idx}s ease-in-out infinite`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 28
                        }}
                      >
                        {style.icon}
                      </Box>
                    ))
                  : prizeIcons.map((icon, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          fontSize: 32,
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.08)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          animation: `floatIcon ${8 + idx}s ease-in-out infinite`,
                          boxShadow: '0 10px 25px rgba(0,0,0,0.4)'
                        }}
                      >
                       {icon}
                      </Box>
                    ))}
              </Stack>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  '@keyframes pulseButton': {
                    '0%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.05)' },
                    '100%': { transform: 'scale(1)' }
                  },
                  '@keyframes qrGlow': {
                    '0%': { boxShadow: '0 20px 40px rgba(0,0,0,0.4)' },
                    '50%': { boxShadow: '0 25px 55px rgba(0,0,0,0.55)' },
                    '100%': { boxShadow: '0 20px 40px rgba(0,0,0,0.4)' }
                  }
                }}
              >
                {isMobile ? (
                  <Button
                    variant="contained"
                    href={qrCodeVal}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      px: 5,
                      py: 2,
                      borderRadius: 4,
                      fontSize: '1.2rem',
                      background:
                        variant === 'vibrant'
                          ? 'linear-gradient(120deg, #ff5f6d, #ffc371)'
                          : 'linear-gradient(120deg, #fbb034, #ffdd00)',
                      color: '#2b1900',
                      animation: 'pulseButton 2s ease-in-out infinite'
                    }}
                    disabled={!qrCodeVal}
                  >
                    Jetzt mitmachen
                  </Button>
                ) : (
                  <Box
                    sx={{
                      position: 'relative',
                      p: variant === 'channel' ? 2.5 : 0,
                      '@keyframes orbitRing': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' }
                      }
                    }}
                  >
                    {variant === 'channel' && (
                      <>
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 4,
                            borderRadius: '24px',
                            border: '1px solid rgba(255,255,255,0.25)',
                            boxShadow: '0 0 30px rgba(0,0,0,0.55)',
                            animation: 'qrGlow 4s ease-in-out infinite',
                            pointerEvents: 'none'
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: -10,
                            borderRadius: '32px',
                            background: 'conic-gradient(from 0deg, rgba(255,120,0,0.3), rgba(0,153,255,0.3), rgba(255,120,0,0.3))',
                            animation: 'orbitRing 12s linear infinite',
                            filter: 'blur(6px)',
                            pointerEvents: 'none'
                          }}
                        />
                      </>
                    )}
                    <Card
                      sx={{
                        backgroundColor: '#fff',
                        borderRadius: 6,
                        p: 2,
                        animation: 'qrGlow 4s ease-in-out infinite',
                        position: 'relative'
                      }}
                    >
                      <QRCode value={qrCodeVal} size={240} />
                    </Card>
                  </Box>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {isMobile ? 'Tippe auf den Button und sichere dir deinen Vorteil.' : 'Einfach Kamera öffnen und scannen.'}
              </Typography>
            </Stack>
          </Card>

          <Card
            sx={{
              p: { xs: 2, md: 3 },
              backgroundColor: 'rgba(5,5,5,0.8)',
              border: '1px solid rgba(255,255,255,0.08)',
              backdropFilter: 'blur(10px)',
              '@keyframes pulseCard': {
                '0%': { borderColor: 'rgba(255,255,255,0.08)' },
                '50%': { borderColor: 'rgba(255,255,255,0.2)' },
                '100%': { borderColor: 'rgba(255,255,255,0.08)' }
              },
              animation: 'pulseCard 8s ease-in-out infinite'
            }}
          >
            <CurrentLottery lotteryData={lottery} />
          </Card>

          {galleryImages.length > 0 && (
            <Card
              sx={{
                p: 2,
                backgroundColor: variant === 'vibrant' ? 'rgba(15,15,20,0.9)' : 'rgba(12,12,12,0.85)',
                border: '1px solid rgba(255,255,255,0.08)',
                backdropFilter: 'blur(14px)',
                overflow: 'hidden'
              }}
            >
              <Marquee pauseOnHover gradient={false} speed={50} direction="right">
                {galleryImages.map((img, idx) => (
                  <Box key={img.id || idx} sx={{ px: 1.5 }}>
                    <Box
                      sx={{
                        width: 330,
                        height: 250,
                        borderRadius: 3,
                        position: 'relative',
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.15)',
                        boxShadow: '0 12px 20px rgba(0,0,0,0.35)',
                        transition: 'transform 0.6s ease',
                        '&:hover': { transform: 'translateY(-6px)' }
                      }}
                    >
                      <Box
                        component="img"
                        src={img.src}
                        alt={img.title || `gallery-${idx}`}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.75) 100%)',
                          display: 'flex',
                          alignItems: 'flex-end',
                          p: 1.2
                        }}
                      > 
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Marquee>
            </Card>
          )}
        </Stack>
      </Container>
    </Box>
  );
}

function NoLotteryView({
  galleryImages = [],
  variant = 'default'
}: {
  galleryImages: GalleryImage[];
  variant?: 'default' | 'vibrant' | 'channel';
}) {
  const emptyMessages = [
    'Bald wartet das nächste Highlight auf dich.',
    'Bleib dran – wir bereiten schon die nächsten Preise vor.',
    'Dein Glücksmoment kommt gleich!'
  ];
  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        color: 'text.primary',
        minHeight: '100vh',
        py: 8,
        position: 'relative',
        overflow: 'hidden',
        background:
          variant === 'channel'
            ? 'radial-gradient(circle at top, rgba(13,17,23,0.9), rgba(2,2,3,1))'
            : variant === 'vibrant'
              ? 'linear-gradient(160deg, rgba(20,20,35,0.9), rgba(0,0,0,0.95))'
              : undefined,
        '&:before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: 'url(/static/prizes/prize1.png)',
          opacity: 0.05,
          filter: 'blur(3px)'
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Logo />
        </Box>
        <Card
          sx={{
            p: 4,
            textAlign: 'center',
            background: 'rgba(10,10,10,0.8)',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Typography variant="h4" sx={{ textTransform: 'uppercase', letterSpacing: 3 }}>
            Aktuell keine Lotterie
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            {emptyMessages[Math.floor(Math.random() * emptyMessages.length)]}
          </Typography>
        </Card>
        {galleryImages.length > 0 && (
          <Box sx={{ mt: 5, position: 'relative' }}>
             
            <Marquee pauseOnHover direction="right" speed={40}>
              {galleryImages.map((img, idx) => (
                <Box key={img.id || idx} sx={{ px: 2 }}>
                  <Card
                    sx={{
                      width: 260,
                      height: 180,
                      borderRadius: 4,
                      overflow: 'hidden',
                      position: 'relative',
                      border: '1px solid rgba(255,255,255,0.12)',
                      boxShadow: '0 15px 30px rgba(0,0,0,0.4)',
                      '&:after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.9))'
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={img.src}
                      alt={img.title || `gallery-${idx}`}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </Card>
                </Box>
              ))}
            </Marquee>
          </Box>
        )}
     </Container>
    </Box>
  );
}

export default function LotteryView({ lottery, galleryImages = [], loading, error, variant = 'default' }: LotteryViewProps) {
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#000'
        }}
      >
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={standaloneTheme}>
      <CssBaseline />
      {lottery && !error ? (
        <HeroView lottery={lottery} galleryImages={galleryImages} variant={variant} />
      ) : (
        <NoLotteryView galleryImages={galleryImages} variant={variant} />
      )}
    </ThemeProvider>
  );
}
